import {
  aggregationSUM,
  aggregationAmount,
  aggregationAmountCustom,
  getOptions,
  initCalculation,
  sortRecords
} from '@/utils/index'

// ==================== 列属性缩写 ====================

const EDIT = { canEdit: true, canModify: true }
const MODIFY = { canModify: true }
const NUM = { fieldType: 'number' }
const DATE = { editor: { type: 'date' } }
const SUM = { aggregation: aggregationSUM }
const AMT = { aggregation: aggregationAmount }

/**
 * 快速创建列定义，将多个属性对象合并到 { field, title } 上。
 */
function col(field, title, ...opts) {
  return Object.assign({ field, title }, ...opts)
}

/**
 * 快速创建下拉选项列属性。
 */
function opts(title, key, names) {
  return getOptions({ title, key, options: names.map(name => ({ name })) })
}

// ==================== 页签 key 定义 ====================

const commonTabKeys = [
  { label: '土地成本', key: 'landCost', value: 'common-land-cost' },
  { label: '建安成本', key: 'constructionCost' },
  { label: '关联成本', key: 'chargingItems', value: 'common-charging-items' },
  { label: '销售成本', key: 'saleCost', value: 'common-sale-cost' },
  { label: '运维成本', key: 'opsCost', value: 'common-ops-cost' },
  { label: '税费利息等', key: 'financeCost', value: 'common-finance-cost' },
  {
    label: '建安收入',
    key: 'constructionIncome',
    value: 'common-construction-income'
  },
  { label: '销售收入', key: 'saleIncome', value: 'common-sale-income' },
  { label: '关联收入', key: 'chargingIncome', value: 'common-charging-income' },
  { label: '运营收入', key: 'opsIncome', value: 'common-ops-income' },
  { label: '其他收入', key: 'otherIncome', value: 'common-other-income' },
  { key: 'investmentCost' }
]

const billValueMap = {
  landCost: { value: 'tcbeLandCost', url: '/common-land-cost' },
  chargingItems: { value: 'tcbeChargingItems', url: '/common-charging-items' },
  saleCost: { value: 'tcbeSaleCost', url: '/common-sale-cost' },
  opsCost: { value: 'tcbeOpsCost', url: '/common-ops-cost' },
  saleIncome: { value: 'tcbeSaleIncome', url: '/common-sale-income' },
  opsIncome: { value: 'tcbeOpsIncome', url: '/common-ops-income' },
  constructionIncome: {
    value: 'tcbeConstructionIncome',
    url: '/common-construction-income'
  },
  chargingIncome: {
    value: 'tcbeChargingIncome',
    url: '/common-relation-income'
  },
  otherIncome: { value: 'tcbeOtherIncome', url: '/common-other-income' },
  cashFlow: { value: 'tcbeCashFlow', url: '/common-cash-flow' },
  financeCost: { value: 'tcbeFinanceCost', url: '/common-finance-cost' }
}

// ==================== 业务类型配置 ====================

const typeMap = {
  investment: {
    amountName: '估算',
    projectUnitValue: 'pie-project-unit-cost',
    investmentLabel: '现金流量',
    bottomMode: 'costBudget',
    projectUnitNumberFields: true,
    withIndicator: false
  },
  design: {
    amountName: '概算',
    projectUnitValue: 'dbe-project-unit-cost',
    investmentLabel: '现金流量',
    bottomMode: 'totalCost',
    projectUnitNumberFields: false,
    withIndicator: true
  },
  bill: {
    amountName: '预算',
    projectUnitValue: 'tcbeProjectUnitCost',
    projectUnitUrl: '/construction-budget-estimate/project-unit-cost',
    investmentValue: 'tcbeInvestmentEstimate',
    investmentUrl: '/common-cash-flow',
    investmentLabel: '现金流量',
    bottomMode: 'costBudget',
    projectUnitNumberFields: false,
    withIndicator: true
  }
}

// ==================== 列构建器 ====================

function createCostColumns(amountName) {
  return [
    col('costName', '费用名称', EDIT, SUM),
    col('unit', '单位', EDIT),
    col('quantity', '数量', NUM, EDIT),
    col('rate', '单价', NUM, EDIT),
    col('baseAmount', '取费基数', MODIFY),
    col('costRatio', '费率', NUM, EDIT, { scale: 5 }),
    col('estimateAmount', `${amountName}金额`, AMT),
    col('estimateAmountTotal', `总${amountName}金额`, AMT),
    col(
      'calculationRule',
      '计算规则',
      EDIT,
      opts('计算规则', 'calculationRule', ['量价', '取费'])
    ),
    col(
      'costType',
      '成本类型',
      EDIT,
      opts('成本类型', 'costType', ['总价', '月度'])
    ),
    col('planStartDate', '计划开始时间', DATE, EDIT),
    col('planEndDate', '计划结束时间', DATE, EDIT)
  ]
}

function createIncomeColumns(costColumns) {
  const titleMap = { 成本类型: '收入类型', 费用名称: '收入名称' }
  return costColumns.map(c => ({ ...c, title: titleMap[c.title] || c.title }))
}

function createFinanceColumns(costColumns) {
  const cols = [...costColumns]
  cols.splice(6, 0, col('interest', '利息', NUM, EDIT, { scale: 6 }))
  cols.splice(
    11,
    0,
    col(
      'costRateRule',
      '利息规则',
      EDIT,
      opts('利息规则', 'costRateRule', ['单利', '复利'])
    )
  )
  return cols
}

const historyBase = [
  col('name', '历史工程名称', SUM),
  col('engineeringClassifyName', '工程分类'),
  col('regionName', '所在地区'),
  col('constructionScale', '建设规模')
]

function createHistoryColumns(mode) {
  if (mode === 'totalCost') {
    return [
      ...historyBase,
      col('totalCost', '总造价', AMT),
      col('totalCostIndicator', '造价指标', AMT),
      col('amount', '设计概算金额', AMT)
    ]
  }
  const nf = mode === 'costBudgetNumber' ? NUM : {}
  return [
    ...historyBase,
    col('costBudget', '总造价', nf, AMT),
    col('costBudgetIndicator', '造价指标', nf, AMT),
    col('adjustPriceCoefficient', '调价系数', NUM, EDIT),
    col('costBudgetIndicatorActual', '调整后的造价指标', AMT, {
      calculation: initCalculation(
        ['costBudgetIndicator', 'adjustPriceCoefficient'],
        'costBudgetIndicator * adjustPriceCoefficient'
      )
    })
  ]
}

const indicatorBase = [
  col('level1Indicator', '一级指标', SUM),
  col('level2Indicator', '二级指标'),
  col('level3Indicator', '三级指标'),
  col('unit', '单位'),
  col('quantity', '核算工程量', EDIT),
  col('rateIndicator', '单价指标'),
  col('quantityIndicator', '单方造价指标')
]

function createIndicatorColumns(mode) {
  if (mode === 'design') {
    return [
      ...indicatorBase,
      col('adjustCoefficient', '调整系数', EDIT),
      col('adjustRateIndicator', '调整后单价指标', AMT),
      col('adjustQuantityIndicator', '调整后单方造价指标', AMT),
      col('amount', '设计概算金额', AMT)
    ]
  }
  return [
    ...indicatorBase,
    col('amount', '设计预算金额', AMT),
    col('adjustPriceCoefficient', '调整系数', EDIT),
    col('amountActual', '调整后设计预算', AMT)
  ]
}

function createLeftColumns(amountName) {
  return [
    col('planDate', '年月'),
    col('estimateCost', `月度${amountName}成本`),
    col('estimateIncome', `月度${amountName}收入`),
    col('inflow', '净流入'),
    col('inflowTotal', '累计净流入')
  ]
}

function createRightColumns() {
  return [
    col('name', '附件名称', { cellType: 'link' }),
    col('unit', '锁号'),
    col('quantity', '联系人'),
    col('sysCreatedDate', '编制日期')
  ]
}

const projectUnitBase = [
  col('name', '单项工程', EDIT, SUM),
  col('measurementUnit', '计量单位', EDIT),
  col('constructionScale', '建设规模', EDIT, {
    aggregation: aggregationAmountCustom(record => !!record.summaryFlag)
  }),
  col('summaryFlag', '汇总标记', EDIT, { multiple: true, cellType: 'checkbox' })
]

function createProjectUnitColumns(config) {
  if (config.type === 'bill') {
    return [
      ...projectUnitBase,
      col('generalContractorAmount', '施工总包', AMT),
      col('professionalEngineeringAmount', '专业工程', AMT),
      col('directDeliveryAmount', '直接发包', AMT),
      col('osMaterialAmount', '甲供材料', AMT),
      col('costRatio', '总包费系数'),
      col('generalContractingServiceAmount', '总包服务费', AMT),
      col('amount', '合计', AMT),
      col('planStartDate', '计划开始时间', DATE, EDIT),
      col('planEndDate', '计划结束时间', DATE, EDIT),
      col('estimateAmount', `${config.amountName}金额`, EDIT, AMT),
      col('costIndicator', '造价指标', EDIT, AMT)
    ]
  }

  const nf = config.projectUnitNumberFields ? NUM : {}
  return [
    ...projectUnitBase,
    col('engineeringClassifyName', '工程分类', MODIFY, {
      editModal: {
        columns: [{ field: 'name', title: '工程分类', tree: true }],
        async options() {
          const res = await this.$service.get(
            '/engineering-classify/listAllClassify',
            {}
          )
          return sortRecords(res?.data || [])
        },
        confirm(selectedCellInfos, res) {
          const row = res[0][0].originData
          selectedCellInfos.forEach(cells => {
            const obj = cells[0].originData
            this.$set(obj, 'engineeringClassifyName', row.name)
            this.$set(obj, 'engineeringClassifyId', row.id)
          })
        }
      }
    }),
    col('regionName', '所在地区', MODIFY, {
      editModal: {
        columns: [{ field: 'name', title: '所在地区' }],
        async options() {
          const res = await this.$service.get('/region/list', {})
          return sortRecords(res?.data || [])
        },
        confirm(selectedCellInfos, res) {
          const row = res[0][0].originData
          selectedCellInfos.forEach(cells => {
            const obj = cells[0].originData
            this.$set(obj, 'regionName', row.name)
            this.$set(obj, 'regionId', row.id)
          })
        }
      }
    }),
    col('address', '详细地址', EDIT),
    col('planStartDate', '计划开始时间', DATE, EDIT),
    col('planEndDate', '计划结束时间', DATE, EDIT),
    col('estimateAmount', `${config.amountName}金额`, EDIT, nf, AMT),
    col('costIndicator', '造价指标', EDIT, nf, AMT)
  ]
}

function createCashFlowColumns(amountName) {
  return [
    col('costName', '名称', { tree: true }),
    col('percentage', '占比(%)'),
    col('estimateCost', `${amountName}成本(万元)`),
    col('estimateIncome', `${amountName}收入(万元)`),
    col('inflow', '净流入(万元)'),
    col('planStartDate', '计划开始年月'),
    col('planEndDate', '计划结束年月')
  ]
}

// ==================== 页签构建 ====================

const incomeKeys = new Set(['saleIncome', 'opsIncome'])
const financeKeys = new Set(['financeCost'])
const cashFlowKeys = new Set(['cashFlow'])

function resolveTopColumns(key, columns) {
  if (incomeKeys.has(key)) return columns.income
  if (financeKeys.has(key)) return columns.finance
  if (cashFlowKeys.has(key)) return columns.cashFlow
  return columns.cost
}

function createTab({ label, value, url, columns, key }) {
  const tab = { label, value, columns, key }
  if (url) tab.url = url
  return tab
}

function createCommonTabs(config, columns) {
  return commonTabKeys.map(item => {
    const { label, key, value } = item

    if (key === 'constructionCost' || key === 'constructionIncome') {
      const isCost = key === 'constructionCost'
      const apiCode = config.projectUnitUrl || config.projectUnitValue
      const tab = createTab({
        label,
        key,
        value: isCost ? config.projectUnitValue : value,
        url: config.projectUnitUrl,
        columns: {
          top: columns.projectUnit,
          bottom: columns.bottom,
          bottomZb: columns.bottomZb,
          left: columns.left,
          right: columns.right
        }
      })
      tab.apiCode = apiCode
      tab.queryType = isCost ? 1 : 2
      return tab
    }

    if (key === 'investmentCost') {
      const tab = createTab({
        label: config.investmentLabel,
        key,
        value: config.investmentValue || 'common-investment-estimate',
        url: config.investmentUrl,
        columns: {
          top: columns.cashFlow,
          bottom: [],
          left: columns.left,
          right: columns.right
        }
      })
      tab.apiCode = '/common-cash-flow'
      return tab
    }

    const billCfg = config.type === 'bill' ? billValueMap[key] : null
    return createTab({
      label,
      key,
      value: billCfg?.value || value,
      url: billCfg?.url,
      columns: {
        top: resolveTopColumns(key, columns),
        bottom: [],
        left: columns.left,
        right: columns.right
      }
    })
  })
}

/**
 * 创建估算/概算/预算详情页签配置。
 *
 * @param {'investment'|'design'|'bill'} type 业务类型
 * @returns {Array} 页签配置
 */
function createEstimateTabs(type) {
  const config = { type, ...typeMap[type] }
  const costCols = createCostColumns(config.amountName)
  const columns = {
    cost: costCols,
    income: createIncomeColumns(costCols),
    finance: createFinanceColumns(costCols),
    projectUnit: createProjectUnitColumns(config),
    cashFlow: createCashFlowColumns(config.amountName),
    bottom: createHistoryColumns(
      type === 'investment' ? 'costBudgetNumber' : config.bottomMode
    ),
    bottomZb: config.withIndicator
      ? createIndicatorColumns(type === 'design' ? 'design' : 'bill')
      : [],
    left: createLeftColumns(config.amountName),
    right: createRightColumns()
  }
  return createCommonTabs(config, columns)
}

/**
 * 创建历史数据页签配置。
 *
 * @returns {Array} 历史数据页签配置
 */
function createEstimateTabsHistory() {
  return [
    { label: '历史工程', value: 'pofeHistoryProject' },
    { label: '工程概况', value: 'projectOverview' }
  ]
}

// ==================== 预构建实例 ====================

const billEstimateTabs = createEstimateTabs('bill')
const designEstimateTabs = createEstimateTabs('design')
const investmentEstimateTabs = createEstimateTabs('investment')
const estimateTabsHistory = createEstimateTabsHistory()

// ==================== 详情页配置 ====================

const commonBaseAmountValues = [
  'common-land-cost',
  'common-charging-items',
  'common-sale-cost',
  'common-finance-cost',
  'common-ops-cost',
  'common-ops-income',
  'common-sale-income'
]

const configDefaults = {
  readOnlyTabValues: ['common-cash-flow', 'common-investment-estimate'],
  baseAmountTabValues: commonBaseAmountValues,
  requestField: 'value',
  historyProjectTabValue: 'pofeHistoryProject',
  projectOverviewTabValue: 'projectOverview',
  costIndicatorLabel: '概算指标',
  costIndicatorUrl: '',
  budgetHistoryUrl: '',
  globalEstimateParamKey: '',
  timeDistributionMainKey: '',
  fileListBasicFieldParams: {},
  showCostIndicator: false,
  showHistoryTabs: false,
  hasHistory: true,
  dynamicHeight: '350px'
}

const estimateDetailConfigMap = {
  investment: {
    ...configDefaults,
    tabs: investmentEstimateTabs,
    tabsHistory: [],
    detailLabel: '估算明细',
    dynamicLabel: '动态估算',
    globalButtonText: '全局估算',
    drButtonText: '带入估算数据',
    drButtonUrl: '/plan-investment-estimate/list',
    drButtonSaveUrl: '/plan-investment-estimate/copy-detail',
    globalEstimateUrl: '/plan-investment-estimate/global-estimate',
    globalEstimateParamKey: 'oieId',
    projectUnitTabValue: 'pie-project-unit-cost',
    budgetHistoryUrl: '/pie-budget-history-data'
  },
  design: {
    ...configDefaults,
    tabs: designEstimateTabs,
    tabsHistory: estimateTabsHistory,
    detailLabel: '概算明细',
    dynamicLabel: '动态概算',
    dynamicHeight: '450px',
    globalButtonText: '全局概算',
    drButtonText: '带入概算数据',
    drButtonUrl: '/design-budget-estimate/list',
    drButtonSaveUrl: '/design-budget-estimate/copy-detail',
    globalEstimateUrl: '/design-budget-estimate/global-estimate',
    projectUnitTabValue: 'dbe-project-unit-cost',
    budgetHistoryUrl: '/dbe-budget-history-data',
    showCostIndicator: true,
    costIndicatorUrl: '/dbe-cost-indicator',
    showHistoryTabs: true
  },
  bill: {
    ...configDefaults,
    tabs: billEstimateTabs,
    tabsHistory: estimateTabsHistory,
    detailLabel: '总预算明细',
    dynamicLabel: '动态预算',
    drButtonText: '带入施工总预算数据',
    globalButtonText: '全局预算',
    drButtonUrl: '/construction-budget-estimate/list',
    drButtonSaveUrl: '/construction-budget-estimate/copy-detail',
    globalEstimateUrl: '/construction-budget-estimate/global-estimate',
    readOnlyTabValues: ['tcbeCashFlow', 'tcbeInvestmentEstimate'],
    baseAmountTabValues: [
      'tcbeLandCost',
      'tcbeChargingItems',
      'tcbeSaleCost',
      'tcbeFinanceCost',
      'tcbeOpsCost',
      'tcbeOpsIncome',
      'tcbeSaleIncome'
    ],
    projectUnitTabValue: 'tcbeProjectUnitCost',
    requestField: 'url',
    hasHistory: false,
    timeDistributionMainKey: 'tcbeId',
    fileListBasicFieldParams: { designOptionsId: 'pofeId' }
  }
}

/**
 * 根据业务类型创建估算详情组件配置。
 *
 * @param {'investment'|'design'|'bill'} type 业务类型
 * @returns {Object} 估算详情组件配置
 */
function createEstimateDetailConfig(type) {
  return estimateDetailConfigMap[type] || estimateDetailConfigMap.investment
}

export {
  createEstimateTabs,
  createEstimateDetailConfig,
  createEstimateTabsHistory,
  billEstimateTabs,
  designEstimateTabs,
  investmentEstimateTabs,
  estimateTabsHistory
}
